import { toast } from 'react-toastify';

import { createDefaultHeaders, getJSONOrThrow } from '@/api';
import { getSession, urlWithAdminQuery } from '@/auth';
import { log } from '@/util';

import { IUpdateContactRequest } from './table';

// Reads a non-2xx response body and surfaces the most useful message to the
// user. The customer-lambdas error envelope can be either `{errorMessage: ...}`
// (Lambda Powertools) or `{message: ...}` (API Gateway validation), and old
// handlers occasionally return plain text. Falls back to a generic prefix when
// none of those parse cleanly.
async function surfaceFetchError(response: Response, fallbackPrefix: string): Promise<void> {
  const rawBody = await response.text().catch(() => '');
  let detail = '';
  if (rawBody) {
    try {
      const parsed = JSON.parse(rawBody);
      detail = parsed?.errorMessage || parsed?.message || rawBody;
    } catch {
      detail = rawBody;
    }
  }
  const message = detail
    ? `${fallbackPrefix} (${response.status}): ${detail}`
    : `${fallbackPrefix} (${response.status})`;
  toast.error(message, { closeButton: true });
  log.error(new Error(message));
}

export const putSubscribers = (body) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/subscriber`, session.adminEmail), {
      method: 'PUT',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (!response.ok) {
          await surfaceFetchError(response, 'Error updating tenants');
        }
        return response;
      })
      .catch((err) => {
        const newErr = new Error(`Error updating tenants [Error: ${err.body}]`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};

export const editSubscriber = (body) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/subscriber/${body.id}`, session.adminEmail), {
      method: 'PUT',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (!response.ok) {
          await surfaceFetchError(response, 'Error updating tenant');
        }
        return response;
      })
      .catch((err) => {
        const newErr = new Error(`Error updating tenant [Error: ${err.body}]`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};

export const removeSubscriberFromUnit = (subscriberId: string, unitId: string) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/subscriber/${subscriberId}/unit/${unitId}`, session.adminEmail), {
      method: 'DELETE',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          await surfaceFetchError(response, 'Error removing tenant from unit');
        }
        return response;
      })
      .catch((err) => {
        const newErr = new Error(`Error removing tenant from unit [Error: ${err.body}]`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};

export const deleteSubscribers = (body) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/subscriber/batchDelete`, session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify(body),
    })
      .then(async (response) => {
        if (!response.ok) {
          await surfaceFetchError(response, 'Error deleting tenants');
        }
        return response;
      })
      .catch((err) => {
        const newErr = new Error(`Error deleting tenants [Error: ${err.body}]`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};

export const updateBillingContact = (request: IUpdateContactRequest, unitId: string) => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/unit/${unitId}/billingContact`, session.adminEmail), {
      method: 'POST',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
      body: JSON.stringify(request),
    })
      .then(async (response) => {
        if (!response.ok) {
          await surfaceFetchError(response, 'Error updating invoice contact');
        }
        return response;
      })
      .catch((err) => {
        const newErr = new Error(`Error updating invoice contact [Error: ${err.body}]`);
        toast.error(newErr.message, { closeButton: true });
        log.error(newErr);
      }),
  );
};

// ──── NOTIFICATION SETTINGS API ────────────────────────────────────────────

export type NotificationFlag = 'LEAK' | 'HIGH_USAGE';

/**
 * Parse subscriber root from API response - handles both {subscriber: {...}} and direct {...} shapes
 *
 * @param data - Raw API response data
 * @returns Subscriber root object
 */
export const parseSubscriberRoot = (data: any) => {
  return data.subscriber ?? data;
};

/**
 * Parse subscriber unit data from subscriber root
 *
 * @param root - Subscriber root object (from parseSubscriberRoot)
 * @param unitId - The unit ID to find
 * @returns Unit subscription object or null if not found
 */
export const parseSubscriberUnit = (root: any, unitId: string) => {
  const unitSubscriptions = root.unit_subscriptions || [];

  // Normalize IDs as strings for comparison
  const targetUnitId = String(unitId);
  return unitSubscriptions.find((u: any) => String(u.unit_id) === targetUnitId) ?? null;
};

/**
 * Parse unit notifications from unit subscription object
 *
 * @param us - Unit subscription object (from parseSubscriberUnit)
 * @returns Object with email and sms notification arrays
 */
export const parseUnitNotifications = (us: any) => {
  return {
    email: us?.enabled_email_notifications || [],
    sms: us?.enabled_sms_notifications || [],
  };
};

/**
 * Get full subscriber details by ID
 *
 * @param subscriberId - The subscriber's ID
 * @returns Promise<any> - Parsed subscriber root object with notification settings
 */
export const getSubscriberById = async (subscriberId: string): Promise<any> => {
  try {
    const session = await getSession();

    const response = await fetch(
      urlWithAdminQuery(`/subscriber/${subscriberId}`, session.adminEmail),
      {
        method: 'GET',
        headers: createDefaultHeaders({
          authToken: session.idToken.toString(),
        }),
      },
    );

    if (response.status === 404) {
      throw new Error('Subscriber not found');
    }

    if (response.status === 403) {
      throw new Error('Access denied');
    }

    const data = await getJSONOrThrow(response);
    return parseSubscriberRoot(data);
  } catch (err: any) {
    // Don't show toast for 404 or 403 errors - let the component handle them
    if (err.message === 'Subscriber not found' || err.message === 'Access denied') {
      throw err;
    }

    const errorMessage = `Error getting subscriber details: ${err.message}`;
    toast.error(errorMessage, { closeButton: true });
    log.error(new Error(errorMessage));
    throw new Error(errorMessage);
  }
};

/**
 * Get subscriber notification settings for a specific unit
 *
 * @param subscriberId - The subscriber's ID
 * @param unitId - The unit ID to get notifications for
 * @returns Promise<string[]> - Array of enabled email notification flags
 */
export const getSubscriberNotifications = async (
  subscriberId: string,
  unitId: string,
): Promise<string[]> => {
  try {
    const session = await getSession();

    const response = await fetch(
      urlWithAdminQuery(`/subscriber/${subscriberId}`, session.adminEmail),
      {
        method: 'GET',
        headers: createDefaultHeaders({
          authToken: session.idToken.toString(),
        }),
      },
    );

    const data = await getJSONOrThrow(response);
    const root = parseSubscriberRoot(data);
    const us = parseSubscriberUnit(root, unitId);
    return parseUnitNotifications(us).email;
  } catch (err: any) {
    const errorMessage = `Error getting subscriber notifications: ${err.message}`;
    toast.error(errorMessage, { closeButton: true });
    log.error(new Error(errorMessage));
    throw new Error(errorMessage);
  }
};

/**
 * Update subscriber notification settings
 *
 * @param subscriberId - The subscriber's ID
 * @param unitId - The unit ID to update notifications for
 * @param emailFlags - Array of email notification flags to enable
 * @param smsFlags - Array of SMS notification flags to enable
 * @returns Promise<{email: string[], sms: string[]}> - Updated notification flags
 */
export const updateSubscriberNotifications = async (
  subscriberId: string,
  unitId: string,
  emailFlags: NotificationFlag[],
  smsFlags: NotificationFlag[] = [],
): Promise<{ email: string[]; sms: string[] }> => {
  const valid: NotificationFlag[] = ['LEAK', 'HIGH_USAGE'];
  if ([...emailFlags, ...smsFlags].some((f) => !valid.includes(f))) {
    throw new Error('Only LEAK and HIGH_USAGE are allowed.');
  }

  const session = await getSession();
  const res = await fetch(urlWithAdminQuery(`/subscriber/${subscriberId}`, session.adminEmail), {
    method: 'PUT',
    headers: createDefaultHeaders({ authToken: session.idToken.toString() }),
    body: JSON.stringify({
      unit_subscriptions: [
        {
          unit_id: unitId,
          enabled_email_notifications: emailFlags,
          enabled_sms_notifications: smsFlags,
        },
      ],
    }),
  });
  const data = await getJSONOrThrow(res);

  // Use the parser functions to handle server response
  const root = parseSubscriberRoot(data);
  const us = parseSubscriberUnit(root, unitId);

  if (!us) {
    // If no matching unit found, return empty arrays
    return {
      email: [],
      sms: [],
    };
  }

  return parseUnitNotifications(us);
};
